<template>
  <div>
    <v-form v-model="valid" ref="mainForm">
      <data-iterator
        :list="sas.employeurs"
        add-new-text="Ajouter une adresse professionnelle"
        :live="{
          endpoint: 'sas-cartes-affaires',
          sasId: sas.id,
          payload: {
            utilisationDeLAdresse: ['Rep'],
            psychotherapie: idValeurCatalogueOui(),
          },
        }"
        :icon="$getIcon('job')"
        card-width="700"
        optional
        :valids="valids"
        ref="dataIterator"
        @addItem="onCarteAffaireCreated"
        @deleteItem="onCarteAffaireDeleted"
      >
        <template v-slot:show-mode="{ item }">
          <h3>{{ estTypeEmploi(item, "Sal") ? item.nomEmployeur : item.raisonSociale }}</h3>
          <p class="my-0">
            {{ item.adresse }}
          </p>
          <p class="my-0">{{ item.ville }}</p>
        </template>
        <template v-slot:show-mode-after-dots="{ item }">
          <template v-if="item.undeletable">
            <opq-case
              :opq-if="!item.suspenduTemporairement"
              code="emploi-inactif"
              v-model="item.inactif"
              :live="liveSasCartesAffaires(2923, item)"
              @eudo-updated="refreshRegroupementsDeTravail"
              optional
            ></opq-case>
            <opq-date-picker
              :opq-if="item.inactif"
              code="emploi-date-fin"
              :live="liveSasCartesAffaires(2940, item)"
              v-model="item.dateFinEmploi"
            ></opq-date-picker>
            <opq-case
              :opq-if="!item.inactif"
              code="emploi-suspendu"
              v-model="item.suspenduTemporairement"
              :live="liveSasCartesAffaires(2947, item)"
              @eudo-updated="refreshRegroupementsDeTravail"
              optional
            />
          </template>
        </template>
        <template v-slot:edit-mode="{ item }">
          <template v-if="!item.inactif">
            <opq-catalogue
              code="emploi-type"
              v-model="item.typeDEmploi"
              :live="liveSasCartesAffaires(2902, item)"
              @eudo-updated="typeEmploiChanged(item)"
              :disabled="item.undeletable"
            >
            </opq-catalogue>
            <opq-text-field
              code="emploi-nb-heure"
              v-model="item.nombreHeures"
              :live="liveSasCartesAffaires(2906, item)"
              is-decimal
              step="0.5"
              customRegex="^\d+([.,][05])?$"
            ></opq-text-field>
            <opq-catalogue
              :opq-if="estTypeEmploi(item, 'Sal')"
              code="emploi-milieu-travail-1"
              v-model="item.milieuTravail1"
              :live="liveSasCartesAffaires(2907, item)"
              @eudo-updated="refreshRegroupementsDeTravail"
              width="500"
              :filter="(fd) => fd.value != item.milieuTravail2 && fd.value != item.milieuTravail3"
            >
            </opq-catalogue>
            <opq-catalogue
              :opq-if="estTypeEmploi(item, 'Sal')"
              code="emploi-milieu-travail-2"
              v-model="item.milieuTravail2"
              :live="liveSasCartesAffaires(2909, item)"
              optional
              width="500"
              :filter="(fd) => fd.value != item.milieuTravail1 && fd.value != item.milieuTravail3"
            >
            </opq-catalogue>
            <opq-catalogue
              :opq-if="estTypeEmploi(item, 'Sal')"
              code="emploi-milieu-travail-3"
              v-model="item.milieuTravail3"
              :live="liveSasCartesAffaires(2910, item)"
              optional
              width="500"
              :filter="(fd) => fd.value != item.milieuTravail1 && fd.value != item.milieuTravail2"
            >
            </opq-catalogue>

            <opq-catalogue
              :opq-if="estTypeEmploi(item, 'Pri')"
              code="emploi-milieu-pratique"
              v-model="item.milieuxDePratique"
              :live="liveSasCartesAffaires(2927, item)"
              optional
            >
            </opq-catalogue>

            <opq-case
              code="emploi-correspondance"
              v-model="item.adresseDeCorrespondance"
              :live="liveSasCartesAffaires(2944, item)"
              optional
              :ref="'adresseCorrespondance' + item.id"
              @eudo-updated="updateAdressesCorrespondances($event, item.id)"
            ></opq-case>

            <search-organisation
              v-if="!item.undeletable && estTypeEmploi(item, 'Sal')"
              @organisation-selected="organisationSelected($event, item.id)"
            ></search-organisation>

            <opq-text-field
              :opq-if="estTypeEmploi(item, 'Sal')"
              code="emploi-organisation"
              v-model="item.nomEmployeur"
              :live="liveSasCartesAffaires(2908, item)"
              :ref="'nomEmployeur' + item.id"
              :disabled="item.undeletable"
            ></opq-text-field>
            <opq-text-field
              :opq-if="estTypeEmploi(item, 'Pri')"
              code="emploi-raison-sociale"
              v-model="item.raisonSociale"
              :live="liveSasCartesAffaires(2932, item)"
              optional
            ></opq-text-field>
            <opq-text-field
              code="emploi-departement"
              :opq-if="estTypeEmploi(item, 'Sal')"
              v-model="item.departement"
              :live="liveSasCartesAffaires(2911, item)"
              optional
            ></opq-text-field>

            <adresse-professionnelle
              :carteAffaire="item"
              :ref="'adresse-' + item.id"
              :disabled="item.undeletable"
            ></adresse-professionnelle>

            <opq-catalogue
              code="informations-confidentielles"
              :opq-if="!isAdresseDomicileProfessionnel(item)"
              v-model="item.informationsConfidentielles"
              :live="liveSasCartesAffaires(2930, item)"
              :ref="'informationsConfidentielles' + item.id"
              optional
            >
            </opq-catalogue>

            <opq-catalogue code="emploi-accessible" v-model="item.accessible" :live="liveSasCartesAffaires(2928, item)">
            </opq-catalogue>

            <div v-show="estNonMembre">
              <div>
                <ressource code="separateur-assurance"></ressource>
              </div>
              <div>
                <ressource code="description-assurance"></ressource>
              </div>
            </div>

            <opq-catalogue
              code="assurance-organisation"
              :opq-if="estTypeEmploi(item, 'Pri') && estNonMembre"
              v-model="item.organisationAUneAssurance"
              :live="liveSasCartesAffaires(2948, item)"
              @eudo-updated="organisationAUneAssuranceChanged(item)"
            >
            </opq-catalogue>

            <opq-alert
              v-if="estNonMembre && estTypeEmploi(item, 'Pri') && estNon(item.organisationAUneAssurance)"
              code="description-assurance-pratique-prive"
            ></opq-alert>
            <opq-alert
              v-if="estNonMembre && estTypeEmploi(item, 'Sal')"
              code="description-assurance-employeur"
            ></opq-alert>
            <opq-catalogue
              :opq-if="estNonMembre && (estTypeEmploi(item, 'Sal') || estOui(item.organisationAUneAssurance))"
              code="emploi-motif-exemption"
              v-model="item.motifExemptionAssurance"
              :live="liveSasCartesAffaires(2924, item)"
              :ref="'motifExemptionAssurance' + item.id"
              :code-exclude="sas.motifsExemptionInaccessibles"
              :disabled="isExemptionDisabled(item)"
              :hidden="isExemptionDisabled(item)"
            >
            </opq-catalogue>
            <opq-alert
              v-show="item.motifExemptionAssurance"
              :code="'information-exemption-assurance-' + getCatalogueCodeById(437, item.motifExemptionAssurance)"
            ></opq-alert>

            <opq-file
              :opq-if="estNonMembre && ['08'].indexOf(getCatalogueCodeById(437, item.motifExemptionAssurance)) > -1"
              code="emploi-attestation-assurance"
              :file="item.attestationExemptionAssurance"
              :live="liveSasCartesAffaires(2922, item)"
            >
            </opq-file>
          </template>
        </template>
      </data-iterator>
      <opq-text-field
        code="emploi-numero-assurance"
        :opq-if="
          estNonMembre &&
          sas.employeurs.some(
            (e) =>
              !e.inactif &&
              ((estTypeEmploi(e, 'Pri') && estNon(e.organisationAUneAssurance)) ||
                (estTypeEmploi(e, 'Sal') && getCatalogueCodeById(437, e.motifExemptionAssurance) == '10')),
          )
        "
        v-model="sas.sasInscriptionAuTableauRegistre.numeroAssurance"
        :live="liveSasInscriptionTableauRegistre(2304)"
        :optional="estRenouvellement"
      ></opq-text-field>
      <opq-relation
        code="domicile-professionnel"
        v-model="sas.sasInscriptionAuTableauRegistre.domicileProfessionnel"
        :list="choixDomicilesProfessionnels"
        :item-text="(e) => affichageTexteDomicileProfessionnel(e)"
        :live="liveSasInscriptionTableauRegistre(2303)"
        ref="domicileProfessionnel"
        width="600"
        :disabled="!!domicileProfessionnelWithMaxHeures && !domicileProfessionnelEstAdressePersonnelle"
      ></opq-relation>
    </v-form>
  </div>
</template>

<script>
import { Api, Queue } from "@/plugins/api"
import { mapGetters } from "vuex"
import CataloguesHelper from "@/mixins/CataloguesHelpers"
import FormHybrid from "@/mixins/FormHybrid"
import SearchOrganisation from "@/components/Common/SearchOrganisation"
import AdresseProfessionnelle from "../FormParts/AdresseProfessionnelle"
import { bus } from "@/plugins/bus"
export default {
  mixins: [CataloguesHelper, FormHybrid],
  components: { SearchOrganisation, AdresseProfessionnelle },
  data() {
    return {
      // Stocke l'id de la dernière adresse professionnelle utilisée comme domicile professionnel.
      lastAdresseDomicileProfessionnel: "",
    }
  },
  props: {
    sas: { type: Object },
  },
  created() {
    bus.$on("adresse-perso-selected-as-correspondance", this.unselectAllAdresseCorrespondance)
  },
  mounted() {
    if (this.sas?.sasInscriptionAuTableauRegistre?.domicileProfessionnel) {
      this.lastAdresseDomicileProfessionnel = this.sas.sasInscriptionAuTableauRegistre.domicileProfessionnel
    }
    if (this.domicileProfessionnelWithMaxHeures) {
      this.$refs.domicileProfessionnel.setValue(this.domicileProfessionnelWithMaxHeures)
    }
  },
  methods: {
    liveSasCartesAffaires(cle, item) {
      return { endpoint: "sas-cartes-affaires", cle: cle, fileId: item.id }
    },
    liveSasInscriptionTableauRegistre(cle) {
      return {
        endpoint: "sas-inscriptions-tableau-registre",
        cle: cle,
        fileId: this.sas.sasInscriptionAuTableauRegistre.id,
      }
    },
    estTypeEmploi(item, type) {
      return this.getCatalogueCodeById(2902, item.typeDEmploi) === type
    },
    estCategorie(codeCategorie) {
      return (
        this.getCatalogueCodeById(11124, this.sas.sasInscriptionAuTableauRegistre.typeDeCotisation) == codeCategorie
      )
    },
    updateAdressesCorrespondances(event, id) {
      if (event) {
        // On décoche les autres
        let ids = this.sas.employeurs.filter((e) => e.adresseDeCorrespondance && e.id != id).map((e) => e.id)
        for (let i = 0; i < ids.length; i++) {
          let ref = this.$refs["adresseCorrespondance" + ids[i]]
          ref.setValue(false)
        }
        bus.$emit("adresse-pro-selected-as-correspondance")
      }
    },
    unselectAllAdresseCorrespondance() {
      let ids = this.sas.employeurs.filter((e) => e.adresseDeCorrespondance).map((e) => e.id)
      for (let i = 0; i < ids.length; i++) {
        let ref = this.$refs["adresseCorrespondance" + ids[i]]
        ref.setValue(false)
      }
    },
    organisationSelected(organisation, id) {
      let adresse = this.$refs["adresse-" + id].$refs.adresse
      this.$refs["nomEmployeur" + id].setValue(organisation.nomEmployeur)
      adresse.$refs.adresse.setValue(organisation.adresse)
      adresse.$refs.complementAdresse.setValue(organisation.complementAdresse)
      adresse.$refs.ville.setValue(organisation.ville)
      adresse.$refs.codePostal.setValue(organisation.codePostal)
      adresse.$refs.province.setValue(organisation.province)
      adresse.$refs.pays.setValue(organisation.pays)
      this.$refs["noteDAcces" + id].setValue(organisation.noteDAcces)
    },
    typeEmploiChanged(item) {
      // Récupérer l'ancien type avant le changement (on le garde dans item.previousTypeDEmploi)
      const oldTypeCode = item.previousTypeDEmploi ? this.getCatalogueCodeById(2902, item.previousTypeDEmploi) : null
      const newTypeCode = this.getCatalogueCodeById(2902, item.typeDEmploi)

      // Mettre à jour l'ancien type pour le prochain changement
      item.previousTypeDEmploi = item.typeDEmploi

      if (this.estTypeEmploi(item, "Sal") && item.serviceDeReference) {
        bus.$emit("decocheServiceReference", item.id)
      }

      // Émettre un événement bus pour notifier le changement de type d'emploi
      if (oldTypeCode == null 
        || (oldTypeCode && oldTypeCode !== newTypeCode)) {
        bus.$emit("sasCarteAffaireTypeChanged", {
          carteAffaire: item,
          oldType: oldTypeCode,
          newType: newTypeCode,
        })
      }

      this.refreshRegroupementsDeTravail()
    },
    /**
     * Appelé quand une nouvelle carte d'affaire est créée via le DataIterator
     */
    onCarteAffaireCreated(carteAffaire) {
      if (carteAffaire) {
        // Initialiser previousTypeDEmploi pour le suivi des changements
        carteAffaire.previousTypeDEmploi = carteAffaire.typeDEmploi
        // Émettre un événement bus pour notifier la création
        bus.$emit("sasCarteAffaireCreated", carteAffaire)
      }
    },
    /**
     * Appelé quand une carte d'affaire est supprimée via le DataIterator
     */
    onCarteAffaireDeleted(carteAffaire) {
      if (carteAffaire) {
        // Émettre un événement bus pour notifier la suppression
        bus.$emit("sasCarteAffaireDeleted", carteAffaire.id)
      }
    },
    refreshRegroupementsDeTravail() {
      bus.$emit("refresh-regroupements-de-travail")
    },
    affichageTexteDomicileProfessionnel(carteAffaire) {
      let result = ""
      if (carteAffaire.nomEmployeur) {
        result += carteAffaire.nomEmployeur + " - "
      }
      if (carteAffaire.raisonSociale) {
        result += carteAffaire.raisonSociale + " - "
      }
      if (carteAffaire.adresse) {
        result += carteAffaire.adresse + ", "
      }
      if (carteAffaire.ville) {
        result += carteAffaire.ville + ", "
      }
      if (carteAffaire.codePostal) {
        result += carteAffaire.codePostal
      }
      return result
    },
    idValeurCatalogueOui() {
      const catOui = this.getCatalogueValueByCode(231, "1")
      return catOui ? catOui.value : null
    },
    estType(codeType) {
      return this.getCatalogueCodeById(2312, this.sas.sasInscriptionAuTableauRegistre.typeDInscription) == codeType
    },
    // Détermine si l'adresse professionnelle est utilisée comme domicile professionnel en fonction du champs "domicileProfessionnel" du sas "InscriptionAuTableauRegistre".
    isAdresseDomicileProfessionnel(item) {
      if (item?.id == this.sas.sasInscriptionAuTableauRegistre.domicileProfessionnel) {
        return true
      }
      return false
    },
    organisationAUneAssuranceChanged(item) {
      if (this.estOui(item.organisationAUneAssurance)) {
        this.$refs["motifExemptionAssurance" + item.id].setValueByCode("08")
      }
    },
    isExemptionDisabled(item) {
      if (this.estOui(item.organisationAUneAssurance)) {
        return true
      }
      return false
    },
  },
  computed: {
    ...mapGetters(["catalogues"]),
    estNonMembre() {
      return this.estCategorie(23) || this.estCategorie(24)
    },
    employeursActifs() {
      return this.sas.employeurs.filter((e) => !e.inactif)
    },
    choixDomicilesProfessionnels() {
      if (this.multipleDomicilesProfessionPsyEgalHeure) {
        return this.employeursActifs.filter((e) => e.nombreHeures == this.maxHeures)
      }
      return [...this.employeursActifs, this.sas.adressePersonnelle]
    },
    maxHeures() {
      if (this.employeursActifs.length == 1) {
        return this.employeursActifs[0].nombreHeures
      }
      if (this.employeursActifs.length > 1) {
        let max = 0
        for (let i = 0; i < this.employeursActifs.length; i++) {
          let current = parseInt(this.employeursActifs[i].nombreHeures) || 0
          if (current > max) {
            max = current
          }
        }
        return max
      }
      return null
    },
    multipleDomicilesProfessionPsyEgalHeure() {
      return this.employeursActifs.filter((e) => e.nombreHeures == this.maxHeures).length > 1
    },
    domicileProfessionnelWithMaxHeures() {
      if (this.employeursActifs.length == 0) {
        return this.sas.adressePersonnelle.id
      }

      if (this.employeursActifs.length == 1) {
        return this.employeursActifs[0].id
      }

      let founds = this.employeursActifs.filter((e) => e.nombreHeures == this.maxHeures)
      if (this.maxHeures) {
        if (founds.length == 1) {
          return founds[0].id
        }
      }
      return null
    },
    domicileProfessionnelEstAdressePersonnelle() {
      return this.domicileProfessionnelWithMaxHeures == this.sas.adressePersonnelle.id
    },
    estRenouvellement() {
      return this.estType(2)
    },
  },
  watch: {
    domicileProfessionnelWithMaxHeures() {
      this.$refs.domicileProfessionnel.setValue(this.domicileProfessionnelWithMaxHeures)
    },
    "sas.employeurs": function () {
      // permet de gérer la suppression d'une carte d'affaire
      this.refreshRegroupementsDeTravail()
    },
    "sas.sasInscriptionAuTableauRegistre.domicileProfessionnel": function (newValue) {
      // Dans tous les cas on sauvegarde la dernière adresse professionnelle utilisée comme domicile professionnel.
      let oldValue = this.lastAdresseDomicileProfessionnel
      this.lastAdresseDomicileProfessionnel = newValue
      
      // Si l'ancienne valeur était vide, il n'y a rien à faire.
      if (oldValue == null || oldValue == "" || oldValue == newValue) {
        return
      }

      // On ne touche pas aux confidentialités de l'adresse personnelle.
      if (oldValue == this.sas.adressePersonnelle.id)
      {
        return
      }

      // On récupère l'adresse professionnelle correspondante.
      let lastAdresse = this.sas.employeurs.find((adresse) => adresse.id == oldValue)
      if (lastAdresse == null) {
        return
      }

      // On met les informations confidentielles par défaut sur l'ancienne adresse professionnelle.
      let catalogueValuesInfosConfidentielles = this.$catalogues.SasCarteAffaire.InformationsConfidentielles
      lastAdresse.informationsConfidentielles = [
        catalogueValuesInfosConfidentielles.AdressePostale,
        catalogueValuesInfosConfidentielles.Courriel,
        catalogueValuesInfosConfidentielles.Telephone,
      ]

      // On utilise nextTick pour s'assurer que le composant est bien render avant d'appeler setValue pour mettre à jour les informations confidentielles.
      this.$nextTick(() => {
        let ref = this.$refs["informationsConfidentielles" + oldValue]
        if (ref) {
          ref.setValue(lastAdresse.informationsConfidentielles, true)
        }
      })
    },
  },
}
</script>

<style></style>
